import React, { useEffect, useRef, useState } from 'react'
import SortType from '../../enums/sort-type.enum';

export type SortOption = {
  name: string;
  value: SortType;
}

type SortProps = {
  options: SortOption[];
  selectedValue: string;
  changeOption: (option: SortOption) => void;
};

const Sort: React.FC<SortProps> = ({ options, selectedValue, changeOption }) => {

  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const selectedOption = options.find((option) => option.value === selectedValue);

  const trigger = useRef<HTMLButtonElement | null>(null);
  const dropdown = useRef<HTMLDivElement | null>(null);

  const updateSortOption = (option: SortOption) => {
    changeOption(option);
    setDropdownOpen(false)
  }

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (!dropdown.current || !trigger.current) return;
      if (!dropdownOpen || dropdown.current.contains(target as Node) || trigger.current.contains(target as Node)) return;
      setDropdownOpen(false);
    };

    document.addEventListener('click', clickHandler);
    return () => document.removeEventListener('click', clickHandler);
  }, [dropdownOpen]);

  useEffect(() => {
    const keyHandler = ({ key }: KeyboardEvent) => {
      if (!dropdownOpen || key !== 'Escape') return;
      setDropdownOpen(false);
    };

    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  }, [dropdownOpen]);

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  });

  return (
    <div className="c-sort relative flex border rounded-lg">
      <button
        className="flex-auto p-2 h-full float-right flex justify-between items-center gap-1.5 rounded-md text-sm hover:text-primary "
        ref={trigger}
        onClick={() => setDropdownOpen(!dropdownOpen)}
      >
        {selectedOption?.name ?? selectedValue}
        <svg
          className="fill-current"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8.00039 11.4C7.85039 11.4 7.72539 11.35 7.60039 11.25L1.85039 5.60005C1.62539 5.37505 1.62539 5.02505 1.85039 4.80005C2.07539 4.57505 2.42539 4.57505 2.65039 4.80005L8.00039 10.025L13.3504 4.75005C13.5754 4.52505 13.9254 4.52505 14.1504 4.75005C14.3754 4.97505 14.3754 5.32505 14.1504 5.55005L8.40039 11.2C8.27539 11.325 8.15039 11.4 8.00039 11.4Z"
            fill=""
          />
        </svg>
      </button>
      <div
        ref={dropdown}
        onFocus={() => setDropdownOpen(true)}
        onBlur={() => setDropdownOpen(false)}
        className={`max-w-39.5 shadow-12 absolute right-0 top-full z-1 mt-1 w-full rounded-[5px] bg-white py-2.5 dark:bg-boxdark ${dropdownOpen === true ? 'block' : 'hidden'
        }`}
      >
        {options.map((option) => (
          <button key={option.value} onClick={() => updateSortOption(option)} className="text-left flex w-full px-4 py-2 text-sm text-black hover:bg-whiter hover:text-primary dark:text-white dark:hover:bg-meta-4">
            {option.name}
          </button>
        ))}
      </div>
    </div>
  );
};

export default Sort;
